<script setup lang="ts">
import { ref } from 'vue';
import { RBtn } from '@therobotcompany/robco-frontend-library';
import { DEFAULT_DEPRECATED_RESOLUTION_ANGLE } from '@/models/RobVision';
import { requiredRule } from '@/helpers/FormRules';
import gettext from '@/i18n/gettext';

const { $gettext } = gettext;

const isOpen = defineModel<boolean>('isOpen', { required: true });

// The user confirmed a deprecated Mask R-CNN training with this rotation
// classifier step. The parent fires the actual training trigger.
const emit = defineEmits<{ confirm: [resolutionAngle: number] }>();

const resolutionAngle = ref<number>(DEFAULT_DEPRECATED_RESOLUTION_ANGLE);

const resolutionAngleRules = [
  requiredRule,
  (v: number) => Number.isInteger(v) || $gettext('Must be a whole number'),
  (v: number) => (v >= 1 && v <= 360) || $gettext('Must be between 1 and 360'),
  (v: number) => 360 % v === 0 || $gettext('Must be a divisor of 360'),
];

const isValid = () =>
  resolutionAngleRules.every((rule) => rule(resolutionAngle.value) === true);

const confirm = () => {
  if (!isValid()) {
    return;
  }
  emit('confirm', resolutionAngle.value);
  isOpen.value = false;
};
</script>

<template>
  <v-dialog v-model="isOpen" max-width="560">
    <v-card>
      <v-card-title class="d-flex align-center">
        <v-icon
          icon="mdi-alert-outline"
          color="warning"
          size="small"
          class="mr-2"
        />
        {{ $gettext('Train Deprecated Model') }}
      </v-card-title>
      <v-card-text>
        <p>
          {{
            $gettext(
              'This trains the previous model generation instead of the current model. Use it only if the RobFlow and Robvision server cannot be updated to the latest version.',
            )
          }}
        </p>
        <p class="text-medium-emphasis">
          {{
            $gettext(
              'Input the rotation angle resolution; 360° disables rotation.',
            )
          }}
        </p>
        <v-text-field
          v-model.number="resolutionAngle"
          :label="$gettext('Resolution Angle')"
          type="number"
          variant="filled"
          density="comfortable"
          suffix="°"
          step="1"
          :rules="resolutionAngleRules"
          class="mt-4"
        />
      </v-card-text>
      <v-card-actions>
        <r-btn variant="outlined" @click="isOpen = false">
          {{ $gettext('Cancel') }}
        </r-btn>
        <v-spacer></v-spacer>
        <r-btn
          variant="accent"
          icon="mdi-clock-start"
          :disabled="!isValid()"
          @click="confirm"
        >
          {{ $gettext('Train Deprecated Model') }}
        </r-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>
